/**
 * As chamadas da tela de glosas (`TP-054`, T11). Tudo o que a tela sabe
 * sobre `/glosas` passa por aqui — mesma disciplina de
 * `features/faturamento/api/faturamento.ts`.
 */

import { queryOptions } from '@tanstack/react-query';

import { requisitar, requisitarComStatus } from '../../../shared/api/clienteHttp';
import { mapearDemonstrativo, mapearGlosa } from './mapeadores';
import type {
  AcaoDaDecisao,
  DecisaoDeGlosaApi,
  DemonstrativoApi,
  DemonstrativoImportado,
  FiltroDaFila,
  Glosa,
  GlosaApi,
  TipoDeGlosa,
} from './tipos';

export const chavesDeGlosas = {
  raiz: ['glosas'] as const,
  fila: (filtro: FiltroDaFila) =>
    [
      ...chavesDeGlosas.raiz,
      'fila',
      filtro.confianca,
      filtro.tipo,
      filtro.area,
      filtro.situacao,
      filtro.demonstrativoId ?? '',
      filtro.hipoteseRegraId ?? '',
    ] as const,
};

/**
 * `GET /glosas`. Sem `demonstrativoId`, `area` nem `hipoteseRegraId` a API sempre filtra por confiança (a
 * baixa é o padrão), por isso `todas` só é enviada com um deles.
 */
export function opcoesDaFila(filtro: FiltroDaFila) {
  return queryOptions({
    queryKey: chavesDeGlosas.fila(filtro),
    queryFn: ({ signal }): Promise<Glosa[]> => {
      const parametros = new URLSearchParams();
      if (filtro.demonstrativoId !== null) parametros.set('demonstrativoId', filtro.demonstrativoId);
      if (filtro.hipoteseRegraId !== null) parametros.set('hipoteseRegraId', filtro.hipoteseRegraId);
      if (filtro.confianca !== 'todas') parametros.set('confianca', filtro.confianca);
      if (filtro.tipo !== '') parametros.set('tipo', filtro.tipo);
      if (filtro.area !== '') parametros.set('area', filtro.area);
      if (filtro.situacao === 'pendentes') parametros.set('pendentes', 'true');
      const querystring = parametros.toString();
      return requisitar<GlosaApi[]>(`/glosas${querystring !== '' ? `?${querystring}` : ''}`, { sinal: signal }).then(
        (itens) => itens.map(mapearGlosa),
      );
    },
  });
}

/**
 * `POST /glosas/demonstrativos`, `multipart/form-data`. `criado` vem do
 * STATUS: `200` é o demonstrativo que já existia pelo (contrato, hash), `201`
 * é novo (mesmo padrão de `importarConferencia`).
 */
export async function importarDemonstrativo(parametros: {
  contratoId: string;
  competencia: string;
  arquivo: File;
}): Promise<{ demonstrativo: DemonstrativoImportado; criado: boolean }> {
  const forma = new FormData();
  forma.append('contratoId', parametros.contratoId);
  forma.append('competencia', parametros.competencia);
  forma.append('file', parametros.arquivo);

  const { dado, status } = await requisitarComStatus<DemonstrativoApi>('/glosas/demonstrativos', {
    method: 'POST',
    corpo: forma,
  });

  return { demonstrativo: mapearDemonstrativo(dado), criado: status === 201 };
}

/** `POST /glosas/{id}/decisao` (`D12`): confirmar ou corrigir, motivo obrigatório, só inclusão. */
export async function decidirGlosa(parametros: {
  glosaId: string;
  acao: AcaoDaDecisao;
  tipoCorrigido: TipoDeGlosa | null;
  motivo: string;
}): Promise<string> {
  const resposta = await requisitar<DecisaoDeGlosaApi>(`/glosas/${encodeURIComponent(parametros.glosaId)}/decisao`, {
    method: 'POST',
    corpo: {
      acao: parametros.acao,
      tipoCorrigido: parametros.acao === 'corrigir' ? parametros.tipoCorrigido : null,
      motivo: parametros.motivo,
    },
  });
  return resposta.decisaoId;
}
